import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005/api/v1';
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
export const WS_BASE_URL = API_ORIGIN.replace(/^http/, 'ws');

let isRefreshing = false;
let refreshSubscribers = [];

export const buildMediaUrl = (path) => {
  if (!path) {
    return '';
  }

  if (path.startsWith('http')) {
    return path;
  }

  return `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Create an axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authAPI = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const subscribeToTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const notifyTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const clearStoredAuth = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

const requestTokenRefresh = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }

  const response = await authAPI.post('/auth/refresh/', { refresh: refreshToken });
  const nextAccessToken = response.data.access;
  if (!nextAccessToken) {
    throw new Error('Refresh response did not include an access token');
  }

  localStorage.setItem('access_token', nextAccessToken);
  if (response.data.refresh) {
    localStorage.setItem('refresh_token', response.data.refresh);
  }

  return nextAccessToken;
};

// Request interceptor for adding authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!token) {
      const applicationKey =
        localStorage.getItem('helpdesk_application_key') ||
        process.env.REACT_APP_HELPDESK_API_KEY;
      const applicationUserId =
        localStorage.getItem('helpdesk_application_user_id') ||
        process.env.REACT_APP_HELPDESK_USER_ID;
      const externalUserId =
        localStorage.getItem('helpdesk_external_user_id') ||
        process.env.REACT_APP_HELPDESK_EXTERNAL_USER_ID;

      if (applicationKey) {
        config.headers['X-Application-Key'] = applicationKey;
      }

      if (applicationUserId) {
        config.headers['X-Application-User-Id'] = applicationUserId;
      } else if (externalUserId) {
        config.headers['X-External-User-Id'] = externalUserId;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');
      const accessToken = localStorage.getItem('access_token');

      if (refreshToken && accessToken && !originalRequest.url?.includes('/auth/')) {
        if (isRefreshing) {
          return new Promise((resolve) => {
            subscribeToTokenRefresh((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const nextAccessToken = await requestTokenRefresh();
          notifyTokenRefreshed(nextAccessToken);
          originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          clearStoredAuth();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (originalRequest.url?.includes('/auth/')) {
        clearStoredAuth();
      } else if (!refreshToken) {
        clearStoredAuth();
      }
    }

    return Promise.reject(error);
  }
);

// API service functions
export const helpdeskAPI = {
  login: (credentials) => authAPI.post('/auth/login/', credentials),
  refreshToken: (refresh) => authAPI.post('/auth/refresh/', { refresh }),
  getCurrentUser: () => api.get('/auth/me/'),

  // Ticket endpoints
  getTicketStatistics: () => api.get('/tickets/statistics/'),
  getTickets: (params = {}) => api.get('/tickets/', { params }),
  getTicket: (id) => api.get(`/tickets/${id}/`),
  createTicket: (data) => api.post('/tickets/', data),
  updateTicket: (id, data) => api.patch(`/tickets/${id}/`, data),
  deleteTicket: (id) => api.delete(`/tickets/${id}/`),
  addComment: (ticketId, data) => {
    // If data is FormData, let axios set the content-type automatically
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return api.post(`/tickets/${ticketId}/add_comment/`, data, config);
  },
  assignTicket: (ticketId, userId) => api.post(`/tickets/${ticketId}/assign/`, { user_id: userId }),
  changeStatus: (ticketId, status) => api.post(`/tickets/${ticketId}/change_status/`, { status }),
  closeTicket: (ticketId) => api.post(`/tickets/${ticketId}/close/`),
  reopenTicket: (ticketId) => api.post(`/tickets/${ticketId}/reopen/`),

  // AI review queue (company-os/strategy/concepts/ai-support-knowledge-loop.md Phase 1)
  getReviewQueue: () => api.get('/tickets/review_queue/'),

  // Knowledge base endpoints
  getKnowledgeEntries: (params = {}) => api.get('/knowledge-entries/', { params }),
  createKnowledgeEntry: (data) => api.post('/knowledge-entries/', data),

  // Application endpoints
  getApplications: () => api.get('/applications/'),

  // Users endpoints
  getUsers: (params = {}) => api.get('/users/', { params }),

  // Category endpoints
  getCategories: (params = {}) => api.get('/categories/', { params }),

  // Priority endpoints
  getPriorities: (params = {}) => api.get('/priorities/', { params }),
};

export default api;
export { clearStoredAuth };
